import { defineMessage, MessageDescriptor } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';
import { faThreads } from '@fortawesome/free-brands-svg-icons/faThreads';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { X_POST_TEXT_LIMIT, xTextLength } from 'fym-shared/src/music24/xPosts';
import { SocialPlatform } from './types';

export type PlatformMediaNeed = 'none' | 'any' | 'video';

export type PostMediaKind = 'image' | 'video';

export interface PlatformMeta {
  platform: SocialPlatform;
  /** The brand's own name. Brand names are not translated. */
  name: string;
  /**
   * The colour this platform's posts carry on the calendar, and the tint of its glyph. A hex
   * value, or a CSS variable when the colour changes with the theme (X: `--x-mark`).
   */
  color: string;
  /**
   * The fill of a solid mark that draws the glyph in white. Absent: `color`. X sets black,
   * because its light ink under a white glyph has no contrast.
   */
  solidColor?: string;
  /** Caption ceiling, per the contract. */
  captionLimit: number;
  icon: IconDefinition;
  /**
   * The hour we slot a post at when nothing better is known.
   *
   * Static per platform in v1: per-account learning does not exist yet, so an
   * account-specific "best slot" would be a number we made up.
   */
  defaultHour: number;
  /** The shape of a handle on this platform, shown as the input's placeholder. */
  handlePlaceholder: string;
  /** This platform takes a 24-hour story. Only Facebook and Instagram do. */
  supportsStories: boolean;
  /**
   * The media a feed post needs here: `none` posts text only, `any` needs a photo or a video,
   * `video` needs a video. music24-api keeps the same table (`PLATFORM_MEDIA_NEEDS`) and refuses
   * a post without it, so the Composer blocks it first.
   */
  mediaNeed: PlatformMediaNeed;
  /**
   * What to keep in mind when writing for this platform.
   *
   * Craft advice, not a measurement: it is the same for every account, and the
   * Composer shows it as a note rather than as a number.
   */
  note: MessageDescriptor;
}

export const platformMeta: Record<SocialPlatform, PlatformMeta> = {
  tiktok: {
    platform: 'tiktok',
    name: 'TikTok',
    color: '#22D3EE',
    captionLimit: 2200,
    icon: faTiktok,
    defaultHour: 19,
    handlePlaceholder: '@yourname',
    supportsStories: false,
    mediaNeed: 'any',
    note: defineMessage({
      id: 'app.sml.promote.platform.tiktok.note',
      defaultMessage: 'Hook them in the first second. Keep the caption short and put the tags at the end.',
    }),
  },
  instagram: {
    platform: 'instagram',
    name: 'Instagram',
    color: '#F0568F',
    captionLimit: 2200,
    icon: faInstagram,
    defaultHour: 18,
    handlePlaceholder: '@yourname',
    supportsStories: true,
    mediaNeed: 'any',
    note: defineMessage({
      id: 'app.sml.promote.platform.instagram.note',
      defaultMessage: 'Only the first line is read. Say where the link goes, because it lives in the bio.',
    }),
  },
  youtube: {
    platform: 'youtube',
    name: 'YouTube',
    color: '#FF4D5E',
    captionLimit: 100,
    icon: faYoutube,
    defaultHour: 16,
    handlePlaceholder: '@yourchannel',
    supportsStories: false,
    mediaNeed: 'video',
    note: defineMessage({
      id: 'app.sml.promote.platform.youtube.note',
      defaultMessage: 'A Short title is 100 characters. Put the song and the artist in it.',
    }),
  },
  facebook: {
    platform: 'facebook',
    name: 'Facebook',
    color: '#5B8DEF',
    captionLimit: 5000,
    icon: faFacebook,
    defaultHour: 13,
    handlePlaceholder: 'your.page',
    supportsStories: true,
    mediaNeed: 'none',
    note: defineMessage({
      id: 'app.sml.promote.platform.facebook.note',
      defaultMessage: 'Longer copy is fine here. One link, and a reason to click it.',
    }),
  },
  threads: {
    platform: 'threads',
    name: 'Threads',
    color: '#B79CFF',
    captionLimit: 500,
    icon: faThreads,
    defaultHour: 12,
    handlePlaceholder: '@yourname',
    supportsStories: false,
    mediaNeed: 'none',
    note: defineMessage({
      id: 'app.sml.promote.platform.threads.note',
      defaultMessage: 'Plain talk works. Ask something, then answer the replies.',
    }),
  },
  x: {
    platform: 'x',
    name: 'X',
    // #E7E9EA on the dark theme, #0F1419 on the light theme (_tokens.scss).
    color: 'var(--x-mark)',
    solidColor: '#000',
    captionLimit: X_POST_TEXT_LIMIT,
    icon: faXTwitter,
    defaultHour: 17,
    handlePlaceholder: '@yourname',
    supportsStories: false,
    mediaNeed: 'none',
    note: defineMessage({
      id: 'app.sml.promote.platform.x.note',
      defaultMessage: 'Short and direct. One line and the link.',
    }),
  },
};

/** The card and calendar order. Fixed, so the six cards never shuffle. X is last. */
export const platformOrder: SocialPlatform[] = ['tiktok', 'instagram', 'youtube', 'facebook', 'threads', 'x'];

export function platformColor(platform: SocialPlatform): string {
  return platformMeta[platform].color;
}

/** The soft fill behind a tinted glyph: the platform colour at 13 %. Works with a CSS variable. */
export function platformSoftFill(platform: SocialPlatform): string {
  return `color-mix(in srgb, ${platformMeta[platform].color} 13%, transparent)`;
}

/** The fill of a solid mark with a white glyph. */
export function platformSolidFill(platform: SocialPlatform): string {
  const meta = platformMeta[platform];
  return meta.solidColor ?? meta.color;
}

export function platformName(platform: SocialPlatform): string {
  return platformMeta[platform].name;
}

/** The length of a caption as the platform counts it. X counts each link as 23 characters. */
export function captionLength(platform: SocialPlatform, text: string): number {
  return platform === 'x' ? xTextLength(text) : text.length;
}

/** True when the caption is longer than the platform takes. */
export function isCaptionOverLimit(platform: SocialPlatform, text: string): boolean {
  return captionLength(platform, text) > platformMeta[platform].captionLimit;
}

/** Whether a story can go out on this platform at all. */
export function supportsStories(platform: SocialPlatform): boolean {
  return platformMeta[platform].supportsStories;
}

/** Whether a post with this media (null: none) can go out on this platform. */
export function platformTakesMedia(platform: SocialPlatform, media: PostMediaKind | null): boolean {
  const need = platformMeta[platform].mediaNeed;
  if (need === 'none') {
    return true;
  }
  if (media === null) {
    return false;
  }
  return need === 'any' || media === 'video';
}

/** The platforms in `platforms` that cannot take a post with this media, in the given order. */
export function platformsMissingMedia(platforms: SocialPlatform[], media: PostMediaKind | null): SocialPlatform[] {
  return platforms.filter((platform) => !platformTakesMedia(platform, media));
}
